// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/packInspector.js
//
// Turns dropped files into brush candidates without touching the DOM, so it
// runs in a worker. A candidate carries the translated settings, the raw
// bytes of its tip/grain/pattern assets and its source metadata; pixels are
// decoded later, only for brushes the user selects.
import { BrushArchiveError, openZip } from './safeZip.js';
import { BRUSH_FORMATS, classifyZip, detectBrushFormat, fileExtension } from './formatDetect.js';
import { PlistError, parseAnyPlist, unarchiveKeyedPlist } from './safePlist.js';
import { extractKppXml, parseKritaBundleManifest, parseKritaBundleMeta, parseKritaPresetXml } from './kritaPreset.js';
import { translateProcreateBrush } from './procreateTranslate.js';
import { KRITA_ENGINE_LABELS, translateKritaPreset } from './kritaTranslate.js';
import { parseMyPaintBrush, translateMyPaintBrush } from './mypaintTranslate.js';
import { parseAbr } from './abrParser.js';
import { scoreCompatibility, toPlainJson } from './brushSchema.js';
import { hashBytes } from './hash.js';

export const MAX_NESTED_ARCHIVE_DEPTH = 2;
export const MAX_FILE_BYTES = 512 * 1024 * 1024;
export const MAX_CANDIDATES = 3000;
const MAX_SOURCE_TEXT = 256 * 1024;

const IMAGE_FORMATS = new Set([BRUSH_FORMATS.PNG, BRUSH_FORMATS.JPEG, BRUSH_FORMATS.WEBP, BRUSH_FORMATS.SVG]);
const NESTED_EXTENSIONS = new Set(['.brushset', '.brush', '.brushlibrary', '.zip', '.bundle', '.kpp', '.abr', '.myb', '.gbr', '.gih']);

function abortCheck(signal) {
  if (signal && signal.aborted) {
    const error = new Error('Import cancelled');
    error.name = 'AbortError';
    throw error;
  }
}

function baseName(path) {
  const clean = String(path || '').replace(/\\/g, '/');
  return clean.slice(clean.lastIndexOf('/') + 1);
}

function stripExtension(path) {
  return baseName(path).replace(/\.[^.]+$/, '') || 'Brush';
}

function dirName(path) {
  const clean = String(path || '');
  const index = clean.lastIndexOf('/');
  return index >= 0 ? clean.slice(0, index) : '';
}

function textOf(bytes, limit = MAX_SOURCE_TEXT) {
  const slice = bytes.length > limit ? bytes.subarray(0, limit) : bytes;
  return typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8').decode(slice) : '';
}

function inferCategory(name, path = '') {
  const text = `${name} ${path}`.toLowerCase();
  if (/eraser|erase|rubber/.test(text)) return 'Erasers';
  if (/ink|liner|pen\b|marker|outline/.test(text)) return 'Inking';
  if (/water|wash|wet|gouache|oil|acryl|paint|blend/.test(text)) return 'Painting';
  if (/pencil|graphite|charcoal|sketch|chalk|pastel|crayon/.test(text)) return 'Sketching';
  if (/texture|grain|noise|speckle|splatter|spray|stipple|dirt|grunge/.test(text)) return 'Texture';
  if (/calligraph|sumi/.test(text)) return 'Calligraphy';
  return 'Imported';
}

function candidate(base) {
  return {
    key: '',
    selectedByDefault: true,
    warnings: [],
    tipSources: [],
    grainSource: null,
    patternSource: null,
    thumbnailBytes: null,
    ...base,
  };
}

// --- Procreate -------------------------------------------------------------

function parseArchive(bytes) {
  return unarchiveKeyedPlist(parseAnyPlist(bytes));
}

function procreateFolders(zip) {
  const folders = new Map();
  zip.entries.forEach((entry) => {
    if (entry.rejected) return;
    const path = entry.path;
    const lower = path.toLowerCase();
    if (/(^|\/)(__macosx|reset)(\/|$)/i.test(path) || /(^|\/)\._/.test(path)) return;
    const name = baseName(lower);
    if (name !== 'brush.archive' && name !== 'shape.png' && name !== 'grain.png') return;
    const dir = dirName(path);
    if (!folders.has(dir)) folders.set(dir, { dir, archive: null, shape: null, grain: null, thumbnail: null });
    const folder = folders.get(dir);
    if (name === 'brush.archive') folder.archive = entry;
    if (name === 'shape.png') folder.shape = entry;
    if (name === 'grain.png') folder.grain = entry;
  });
  folders.forEach((folder) => {
    folder.thumbnail = zip.find((folder.dir ? folder.dir + '/' : '') + 'QuickLook/Thumbnail.png');
  });
  return folders;
}

function readBrushsets(zip, warnings = []) {
  const sets = [];
  zip.entries.filter((entry) => baseName(entry.path).toLowerCase() === 'brushset.plist' && !entry.rejected).forEach((entry) => {
    try {
      const plist = parseAnyPlist(zip.read(entry));
      sets.push({
        dir: dirName(entry.path),
        name: String((plist && plist.name) || '').trim(),
        order: Array.isArray(plist && plist.brushes) ? plist.brushes.map(String) : [],
      });
    } catch (err) {
      warnings.push(`${entry.path}: set name and order could not be read (${err && err.message ? err.message : "invalid plist"}).`);
    }
  });
  return sets;
}

function inspectProcreateZip(zip, context, out) {
  const folders = procreateFolders(zip);
  const sets = readBrushsets(zip, context.warnings);
  const subFolders = new Set(Array.from(folders.keys()).filter((dir) => /\/sub\d+$/i.test(dir)));
  const fallbackSetName = context.packName;
  const primary = Array.from(folders.values()).filter((folder) => folder.archive && !subFolders.has(folder.dir));

  const setFor = (dir) => sets.find((set) => (set.dir ? dir.startsWith(set.dir + '/') || dir === set.dir : true)) || null;
  const orderOf = (folder) => {
    const set = setFor(folder.dir);
    if (!set) return 1e6;
    const index = set.order.indexOf(baseName(folder.dir));
    return index >= 0 ? index : 1e5;
  };
  primary.sort((a, b) => orderOf(a) - orderOf(b) || a.dir.localeCompare(b.dir));

  primary.forEach((folder, index) => {
    abortCheck(context.signal);
    const set = setFor(folder.dir);
    const setName = (set && set.name) || fallbackSetName;
    let root = null;
    let parseError = '';
    try {
      root = parseArchive(zip.read(folder.archive));
    } catch (err) {
      parseError = err instanceof PlistError || err instanceof BrushArchiveError ? err.message : 'unreadable Brush.archive';
    }
    const name = String((root && root.name) || '').trim() || `${setName || 'Procreate'} brush ${index + 1}`;
    const dualBrush = subFolders.has(folder.dir + '/Sub01') || Array.from(subFolders).some((dir) => dirName(dir) === folder.dir);
    const translation = root ? translateProcreateBrush(root, { hasGrain: !!folder.grain, dualBrush }) : null;
    const warnings = [];
    let compatibility = translation ? translation.compatibility : null;
    const tipSources = [];
    if (folder.shape) {
      tipSources.push({ kind: 'image', entryPath: folder.shape.path, bytes: zip.read(folder.shape), polarity: translation ? translation.settings.tipPolarity : 'light' });
    } else {
      const builtIn = root && root.bundledShapePath ? String(root.bundledShapePath) : '';
      warnings.push(builtIn ? `Shape "${builtIn}" is from Procreate's built-in library and is not in the file; a round tip is used.` : 'Shape.png is missing; a round tip is used.');
      tipSources.push({ kind: 'procedural', shape: { kind: 'round', hardness: 0.85 } });
      compatibility = scoreCompatibility({
        ...(compatibility || {}),
        missing: [...((compatibility && compatibility.missing) || []).map((label) => ({ label, major: /\(major\)$/.test(label) })), { label: 'shape source missing from the file', major: true }],
      });
    }
    if (!folder.grain && root && root.bundledGrainPath && !/blank/i.test(String(root.bundledGrainPath))) {
      warnings.push(`Grain "${root.bundledGrainPath}" is from Procreate's built-in library and is not in the file; no grain is applied.`);
      compatibility = scoreCompatibility({
        ...(compatibility || {}),
        missing: [...((compatibility && compatibility.missing) || []).map((label) => ({ label, major: /\(major\)$/.test(label) })), { label: 'built-in grain not included in the file', major: false }],
      });
    }
    if (parseError) {
      warnings.push(`Brush settings could not be read (${parseError}); only the shape is imported.`);
      compatibility = scoreCompatibility({ implemented: ['shape tip'], missing: [{ label: 'brush settings unreadable', major: true }] });
    }

    out.push(candidate({
      key: `${context.fileKey}:${folder.dir || 'root'}`,
      name,
      folderPath: setName || 'Procreate',
      category: inferCategory(name),
      app: 'procreate',
      format: context.format,
      sourceEngine: 'procreate.silica',
      engineLabel: 'Procreate brush',
      translation: translation ? { ...translation, compatibility } : {
        settings: { spacing: 0.1, opacity: 1, flow: 1, tipPolarity: 'light', opacityMode: 'stroke' },
        dynamics: {},
        texture: null,
        rendering: { mode: 'glaze' },
        compatibility,
      },
      engine: { id: 'stamp', params: {} },
      tipSources,
      grainSource: folder.grain ? { kind: 'image', entryPath: folder.grain.path, bytes: zip.read(folder.grain) } : null,
      thumbnailBytes: folder.thumbnail && folder.thumbnail.size < 4 * 1024 * 1024 ? zip.read(folder.thumbnail) : null,
      warnings,
      source: {
        app: 'procreate',
        format: context.format,
        engine: 'procreate.silica',
        packName: setName || context.packName,
        folderPath: folder.dir,
        entryPath: folder.archive.path,
        fileName: context.fileName,
        author: translation ? translation.author : '',
        raw: root ? toPlainJson(root) : null,
      },
      hashParts: [folder.archive.path, folder.archive.size, folder.shape ? folder.shape.size : 0, folder.grain ? folder.grain.size : 0],
      hashBytesList: [zip.read(folder.archive)],
    }));
  });

  if (!primary.length) context.warnings.push(`${context.fileName}: no Procreate brush folders (Brush.archive) found.`);
}

// --- Krita -----------------------------------------------------------------

function kritaResourceIndex(zip, embedded = []) {
  const brushes = [];
  const patterns = [];
  (zip ? zip.entries : []).forEach((entry) => {
    if (entry.rejected) return;
    const lower = entry.path.toLowerCase();
    const ext = fileExtension(lower);
    if (!['.gbr', '.gih', '.png', '.svg', '.abr', '.jpg', '.jpeg', '.pat', '.webp'].includes(ext)) return;
    if (lower.startsWith('preview') || lower.includes('/preview') || lower.startsWith('meta-inf/')) return;
    const record = { path: entry.path, name: baseName(entry.path), entry, md5: '' };
    if (/(^|\/)patterns?\//.test(lower) || /(^|\/)textures?\//.test(lower) || ext === '.pat') patterns.push(record);
    else brushes.push(record);
  });
  const manifest = zip ? zip.find('META-INF/manifest.xml') : null;
  if (manifest && !manifest.rejected) {
    try {
      parseKritaBundleManifest(zip.readText(manifest)).forEach((item) => {
        const match = [...brushes, ...patterns].find((record) => record.path === item.path);
        if (match) match.md5 = String(item.md5sum || '').toLowerCase();
      });
    } catch {}
  }
  embedded.forEach((resource) => {
    const target = resource.type === 'patterns' ? patterns : brushes;
    target.push({ path: resource.type + '/' + resource.filename, name: resource.filename, md5: String(resource.md5sum || '').toLowerCase(), base64: resource.base64 });
  });
  return { brushes, patterns };
}

function decodeBase64(text) {
  const clean = String(text || '').replace(/\s+/g, '');
  if (!clean || clean.length > 64 * 1024 * 1024) return null;
  try {
    if (typeof atob === 'function') {
      const binary = atob(clean);
      const out = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
      return out;
    }
    // eslint-disable-next-line no-undef
    return new Uint8Array(Buffer.from(clean, 'base64'));
  } catch {
    return null;
  }
}

function findResource(list, filename, md5) {
  const wantedMd5 = String(md5 || '').toLowerCase();
  const wantedName = baseName(String(filename || '').replace(/\\/g, '/')).toLowerCase();
  if (wantedMd5) {
    const byMd5 = list.find((item) => item.md5 && item.md5 === wantedMd5);
    if (byMd5) return byMd5;
  }
  return list.find((item) => item.name.toLowerCase() === wantedName) || null;
}

function resourceBytes(zip, resource) {
  if (!resource) return null;
  if (resource.base64) return decodeBase64(resource.base64);
  return zip ? zip.read(resource.entry) : null;
}

function kritaTipSource(format, bytes, translation, entryPath) {
  const polarity = translation.tipColour === 'image' ? 'image' : 'krita';
  if (format === BRUSH_FORMATS.GBR) return { kind: 'gbr', bytes, entryPath, polarity };
  if (format === BRUSH_FORMATS.GIH) return { kind: 'gih', bytes, entryPath, polarity };
  if (format === BRUSH_FORMATS.ABR) return { kind: 'abr-first', bytes, entryPath, polarity };
  return { kind: 'image', bytes, entryPath, polarity };
}

function inspectKritaPreset(bytes, entryPath, zip, index, context, out) {
  const xml = extractKppXml(bytes);
  if (!xml) {
    context.warnings.push(`${baseName(entryPath)}: no Krita preset data found.`);
    return;
  }
  const preset = parseKritaPresetXml(xml);
  const translation = translateKritaPreset(preset);
  const embedded = preset.embeddedResources || [];
  const resources = index || kritaResourceIndex(null, embedded);
  if (index && embedded.length) kritaResourceIndex(null, embedded).brushes.forEach((item) => resources.brushes.push(item));
  const warnings = [];
  const tipSources = [];
  const brush = translation.brush || {};
  let compatibility = translation.compatibility;
  const missingMajor = (label) => {
    compatibility = scoreCompatibility({
      implemented: compatibility.implemented,
      approximated: compatibility.approximated,
      missing: [...compatibility.missing.map((item) => ({ label: item, major: /\(major\)$/.test(item) })), { label, major: true }],
      notes: compatibility.notes,
      unrenderable: compatibility.status === 'unsupported',
    });
  };

  if (translation.engine.id === 'stamp') {
    if (translation.brushType === 'auto_brush' || !translation.brushType) {
      tipSources.push({ kind: 'procedural', shape: { kind: 'krita-auto', mask: translation.mask || {}, brush } });
    } else {
      const found = findResource(resources.brushes, brush.filename, brush.md5sum);
      const data = resourceBytes(zip, found);
      if (data) {
        const detected = detectBrushFormat(data, found.name);
        tipSources.push(kritaTipSource(detected.format, data, translation, found.path));
      } else {
        warnings.push(`Brush tip "${brush.filename || brush.md5sum || 'unknown'}" is not in the pack; a soft round tip is used.`);
        tipSources.push({ kind: 'procedural', shape: { kind: 'round', hardness: 0.6 } });
        missingMajor(`brush tip "${brush.filename || 'unknown'}" missing from the pack`);
      }
    }
  }

  let patternSource = null;
  if (translation.texture) {
    const found = findResource(resources.patterns, translation.texture.patternFileName, translation.texture.patternMd5);
    let data = resourceBytes(zip, found);
    let embeddedPattern = null;
    if (!data && translation.texture.embeddedPattern) {
      // Krita embeds the pattern PNG in the preset, base64-encoded twice.
      const once = decodeBase64(translation.texture.embeddedPattern);
      const text = once && once.length > 8 ? String.fromCharCode(...once.subarray(0, 8)) : '';
      data = text.startsWith('iVBOR') ? decodeBase64(new TextDecoder('latin1').decode(once)) : once;
      if (data) embeddedPattern = { name: (translation.texture.patternFileName || 'pattern.png').split('/').pop(), path: 'embedded pattern' };
    }
    delete translation.texture.embeddedPattern;
    const patternRef = found || embeddedPattern;
    if (data && patternRef && fileExtension(patternRef.name) !== '.pat' && (data[0] === 137 || data[0] === 0xff)) {
      patternSource = { kind: 'image', bytes: data, entryPath: patternRef.path };
    } else {
      warnings.push(`Pattern "${translation.texture.patternFileName || 'unknown'}" is ${data ? 'a .pat file (not supported yet)' : 'not in the pack'}; texture is skipped.`);
      translation.texture = null;
      compatibility = scoreCompatibility({
        implemented: compatibility.implemented.filter((item) => item !== 'pattern texture'),
        approximated: compatibility.approximated,
        missing: [...compatibility.missing.map((item) => ({ label: item, major: /\(major\)$/.test(item) })), { label: 'texture pattern missing', major: false }],
        notes: compatibility.notes,
        unrenderable: compatibility.status === 'unsupported',
      });
    }
  }

  const name = preset.name || stripExtension(entryPath).replace(/_/g, ' ');
  out.push(candidate({
    key: `${context.fileKey}:${entryPath}`,
    name,
    folderPath: context.packName || 'Krita',
    category: inferCategory(name, entryPath),
    app: 'krita',
    format: context.format,
    sourceEngine: translation.sourceEngine,
    engineLabel: KRITA_ENGINE_LABELS[preset.paintopid] || preset.paintopid,
    translation: { ...translation, compatibility },
    engine: translation.engine,
    tipSources,
    patternSource,
    thumbnailBytes: bytes.length < 4 * 1024 * 1024 && bytes[0] === 137 ? bytes : null,
    warnings,
    selectedByDefault: compatibility.status !== 'unsupported',
    source: {
      app: 'krita',
      format: context.format,
      engine: translation.sourceEngine,
      packName: context.packName,
      folderPath: dirName(entryPath),
      entryPath,
      fileName: context.fileName,
      author: context.author || '',
      license: context.license || '',
      raw: toPlainJson({ paintopid: preset.paintopid, params: Object.fromEntries(preset.order.map((key) => [key, preset.params[key].value.length > 4096 ? preset.params[key].value.slice(0, 4096) : preset.params[key].value])) }),
      sourceText: xml.length <= MAX_SOURCE_TEXT ? xml : '',
    },
    hashBytesList: [new TextEncoder().encode(xml)],
  }));
}

function inspectKritaZip(zip, context, out, isBundle) {
  if (isBundle) {
    const meta = zip.find('meta.xml');
    if (meta && !meta.rejected) {
      try {
        const info = parseKritaBundleMeta(zip.readText(meta));
        if (info.name) context.packName = info.name;
        context.author = info.author;
        context.license = info.license;
      } catch {}
    }
  }
  const index = kritaResourceIndex(zip);
  const presets = zip.entries.filter((entry) => !entry.rejected && fileExtension(entry.path) === '.kpp');
  presets.forEach((entry) => {
    abortCheck(context.signal);
    try {
      inspectKritaPreset(zip.read(entry), entry.path, zip, index, context, out);
    } catch (err) {
      if (err && err.name === 'AbortError') throw err;
      context.warnings.push(`${baseName(entry.path)}: ${err && err.message ? err.message : 'could not be read'}`);
    }
  });
  return presets.length;
}

// --- Loose tips, MyPaint, ABR ---------------------------------------------

function tipCandidate(bytes, format, path, context, extra = {}) {
  const name = extra.name || stripExtension(path).replace(/[_-]+/g, ' ').trim();
  const kind = format === BRUSH_FORMATS.GBR ? 'gbr' : format === BRUSH_FORMATS.GIH ? 'gih' : 'image';
  const app = format === BRUSH_FORMATS.GBR || format === BRUSH_FORMATS.GIH ? 'gimp' : 'image';
  const implemented = ['brush tip'];
  if (kind === 'gbr') implemented.push('GBR spacing');
  if (kind === 'gih') implemented.push('animated tip cells and selection mode');
  return candidate({
    key: `${context.fileKey}:${path}`,
    name,
    folderPath: context.packName || 'Imported tips',
    category: inferCategory(name, path),
    app,
    format,
    sourceEngine: app === 'gimp' ? 'gimp.' + kind : 'image',
    engineLabel: kind === 'gih' ? 'GIMP image pipe' : kind === 'gbr' ? 'GIMP brush' : 'Image tip',
    translation: {
      settings: { spacing: 0.12, opacity: 1, flow: 1, opacityMode: 'stroke', tipPolarity: 'auto' },
      dynamics: { size: { pressure: { amount: 1, curve: [] } }, rotation: { follow: 0 } },
      texture: null,
      rendering: { mode: 'glaze' },
      compatibility: scoreCompatibility({ implemented, notes: ['A brush tip carries no dynamics of its own; Voxemera defaults are applied (pressure → size).'] }),
    },
    engine: { id: 'stamp', params: {} },
    tipSources: [{ kind, bytes, entryPath: path, polarity: 'auto' }],
    source: { app, format, engine: app, packName: context.packName, entryPath: path, fileName: context.fileName },
    hashBytesList: [bytes],
    ...extra,
  });
}

function inspectMyPaint(bytes, path, context, out) {
  const brush = parseMyPaintBrush(textOf(bytes, 2 * 1024 * 1024));
  const name = stripExtension(path).replace(/[_-]+/g, ' ').trim();
  const translation = translateMyPaintBrush(brush, { name });
  out.push(candidate({
    key: `${context.fileKey}:${path}`,
    name,
    folderPath: context.packName || 'MyPaint',
    category: inferCategory(name, path),
    app: 'mypaint',
    format: BRUSH_FORMATS.MYB,
    sourceEngine: 'mypaint',
    engineLabel: 'MyPaint brush',
    translation,
    engine: { id: 'stamp', params: {} },
    tipSources: [{ kind: 'procedural', shape: translation.proceduralTip }],
    source: { app: 'mypaint', format: 'myb', engine: 'mypaint', packName: context.packName, entryPath: path, fileName: context.fileName, raw: toPlainJson(brush), sourceText: textOf(bytes) },
    hashBytesList: [bytes],
    selectedByDefault: translation.compatibility.status !== 'unsupported',
  }));
}

function inspectAbr(bytes, path, context, out) {
  const parsed = parseAbr(bytes);
  parsed.warnings.slice(0, 20).forEach((warning) => context.warnings.push(`${baseName(path)}: ${warning}`));
  const packName = context.packName || stripExtension(path);
  parsed.brushes.forEach((brush, index) => {
    const missing = [{ label: 'Photoshop brush dynamics (shape dynamics, scattering, texture, dual brush, colour dynamics, transfer) are not read; only the sampled tip' + (brush.hasSpacing ? ' and spacing are' : ' is') + ' imported', major: true }];
    out.push(candidate({
      key: `${context.fileKey}:${path}#${index}`,
      name: brush.name,
      folderPath: packName,
      category: inferCategory(brush.name, path),
      app: 'photoshop',
      format: BRUSH_FORMATS.ABR,
      sourceEngine: `photoshop.abr${parsed.version}`,
      engineLabel: `Photoshop ABR v${parsed.version}`,
      translation: {
        settings: { spacing: brush.spacing, opacity: 1, flow: 1, opacityMode: 'stroke', tipPolarity: 'alpha' },
        dynamics: { size: { pressure: { amount: 1, curve: [] } } },
        texture: null,
        rendering: { mode: 'glaze' },
        compatibility: scoreCompatibility({ implemented: ['sampled tip', ...(brush.hasSpacing ? ['spacing'] : [])], missing }),
      },
      engine: { id: 'stamp', params: {} },
      tipSources: [{ kind: 'gray', width: brush.width, height: brush.height, pixels: brush.pixels, polarity: 'alpha' }],
      source: { app: 'photoshop', format: 'abr', engine: `abr${parsed.version}`, packName, entryPath: path + '#' + (index + 1), fileName: context.fileName, raw: { version: parsed.version, index, width: brush.width, height: brush.height } },
      hashBytesList: [brush.pixels],
    }));
  });
  if (!parsed.brushes.length) context.warnings.push(`${baseName(path)}: no sampled brushes found.`);
}

// --- Dispatcher ------------------------------------------------------------

function isPreviewImage(path) {
  const lower = path.toLowerCase();
  const name = baseName(lower);
  return /(^|\/)(preview|previews|thumbnails?|quicklook|meta-inf|__macosx|signature|authorpicture)(\/|$)/.test(lower)
    || /^(preview|thumbnail|thumb|icon|cover|readme)/.test(name)
    || name.startsWith('._');
}

function inspectGenericZip(zip, context, out, depth) {
  const krita = zip.entries.some((entry) => fileExtension(entry.path) === '.kpp');
  let presets = 0;
  if (krita) presets = inspectKritaZip(zip, { ...context, format: 'zip' }, out, false);
  const nested = zip.entries.filter((entry) => !entry.rejected && NESTED_EXTENSIONS.has(fileExtension(entry.path)) && fileExtension(entry.path) !== '.kpp');
  const tipsInsideKrita = krita ? new Set(kritaResourceIndex(zip).brushes.map((item) => item.path)) : new Set();
  for (const entry of nested) {
    abortCheck(context.signal);
    if (tipsInsideKrita.has(entry.path) && presets > 0) continue;
    if (depth >= MAX_NESTED_ARCHIVE_DEPTH && ['.zip', '.brushset', '.bundle', '.brushlibrary', '.brush'].includes(fileExtension(entry.path))) {
      context.warnings.push(`${entry.path}: nested more than ${MAX_NESTED_ARCHIVE_DEPTH} archives deep; skipped.`);
      continue;
    }
    try {
      inspectBytes(zip.read(entry), entry.path, { ...context, packName: stripExtension(entry.path), fileKey: `${context.fileKey}/${entry.path}` }, out, depth + 1);
    } catch (err) {
      if (err && err.name === 'AbortError') throw err;
      context.warnings.push(`${entry.path}: ${err && err.message ? err.message : 'could not be read'}`);
    }
  }
  if (!presets) {
    zip.entries
      .filter((entry) => !entry.rejected && ['.png', '.jpg', '.jpeg', '.webp'].includes(fileExtension(entry.path)) && !isPreviewImage(entry.path))
      .forEach((entry) => {
        if (out.length >= MAX_CANDIDATES) return;
        const bytes = zip.read(entry);
        const detected = detectBrushFormat(bytes, entry.path);
        if (IMAGE_FORMATS.has(detected.format)) out.push(tipCandidate(bytes, detected.format, entry.path, { ...context, packName: dirName(entry.path) || context.packName }));
      });
  }
}

export function inspectBytes(bytes, fileName, context, out, depth = 0) {
  abortCheck(context.signal);
  if (bytes.length > MAX_FILE_BYTES) throw new Error(`${fileName} is larger than ${Math.round(MAX_FILE_BYTES / 1048576)} MB.`);
  const detected = detectBrushFormat(bytes, fileName);
  const ctx = { ...context, fileName: context.fileName || fileName, packName: context.packName || stripExtension(fileName), format: detected.format };
  switch (detected.format) {
    case BRUSH_FORMATS.ZIP: {
      const zip = openZip(bytes);
      zip.rejected.slice(0, 10).forEach((entry) => context.warnings.push(`${fileName}: skipped "${entry.rawName}" (${entry.rejected}).`));
      const kind = classifyZip(zip, fileName);
      if (kind === 'krita-bundle') inspectKritaZip(zip, { ...ctx, format: 'krita-bundle' }, out, true);
      else if (kind === 'procreate-brush' || kind === 'procreate-brushset' || kind === 'procreate-library') inspectProcreateZip(zip, { ...ctx, format: kind }, out);
      else inspectGenericZip(zip, ctx, out, depth);
      break;
    }
    case BRUSH_FORMATS.KPP:
      inspectKritaPreset(bytes, fileName, null, null, { ...ctx, format: 'kpp' }, out);
      break;
    case BRUSH_FORMATS.MYB:
      inspectMyPaint(bytes, fileName, ctx, out);
      break;
    case BRUSH_FORMATS.ABR:
      inspectAbr(bytes, fileName, ctx, out);
      break;
    case BRUSH_FORMATS.GBR:
    case BRUSH_FORMATS.GIH:
    case BRUSH_FORMATS.PNG:
    case BRUSH_FORMATS.JPEG:
    case BRUSH_FORMATS.WEBP:
    case BRUSH_FORMATS.SVG:
      out.push(tipCandidate(bytes, detected.format, fileName, ctx));
      break;
    default:
      context.warnings.push(`${fileName}: not a recognised brush format.`);
  }
}

// files: [{ name, bytes: Uint8Array }]. Returns candidates with a content
// hash per brush; the hash ignores names so renamed duplicates are found.
export async function inspectBrushFiles(files, { signal, onProgress } = {}) {
  const candidates = [];
  const warnings = [];
  const failures = [];
  const list = Array.isArray(files) ? files : [];
  for (let index = 0; index < list.length; index += 1) {
    abortCheck(signal);
    const file = list[index];
    if (typeof onProgress === 'function') onProgress({ phase: 'inspect', index, total: list.length, fileName: file.name });
    const before = candidates.length;
    try {
      inspectBytes(file.bytes, file.name, { fileKey: `${index}:${file.name}`, fileName: file.name, packName: '', warnings, signal }, candidates, 0);
    } catch (err) {
      if (err && err.name === 'AbortError') throw err;
      failures.push({ fileName: file.name, message: err && err.message ? err.message : 'could not be read' });
    }
    if (candidates.length === before && !failures.some((failure) => failure.fileName === file.name)) {
      failures.push({ fileName: file.name, message: 'no brushes found' });
    }
    if (candidates.length > MAX_CANDIDATES) {
      candidates.length = MAX_CANDIDATES;
      warnings.push(`Only the first ${MAX_CANDIDATES} brushes are shown.`);
      break;
    }
  }
  for (let index = 0; index < candidates.length; index += 1) {
    abortCheck(signal);
    const item = candidates[index];
    const parts = [...(item.hashBytesList || [])];
    item.tipSources.forEach((tip) => {
      if (tip.bytes) parts.push(tip.bytes);
      if (tip.pixels) parts.push(tip.pixels);
    });
    if (item.grainSource && item.grainSource.bytes) parts.push(item.grainSource.bytes);
    item.contentHash = await hashBytes(parts);
    delete item.hashBytesList;
    delete item.hashParts;
    if (typeof onProgress === 'function' && index % 25 === 0) onProgress({ phase: 'hash', index, total: candidates.length });
  }
  return { candidates, warnings, failures };
}
